import { useCallback, useEffect, useRef, useState } from 'react';
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { ClientProf, SuiviDeClasse as Suivi } from './api-prof.js';
import { progressionEnCsv, telechargerCsv } from './csv.js';

/** Rafraîchissement automatique, comme le demande le plan. */
export const INTERVALLE_RAFRAICHISSEMENT_MS = 15_000;

const dureeLisible = (secondes: number | null): string => {
  if (secondes === null) return '';
  const minutes = Math.floor(secondes / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60}`;
};

/** Les jauges en miniature : une pastille par indicateur, avec sa valeur. */
function JaugesMiniatures({
  content,
  gauges,
}: {
  content: Content;
  gauges: Record<string, number> | null;
}) {
  if (!gauges) {
    return <span className="text-slate-400">{T.prof.aucuneSolution}</span>;
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {content.jauges.map((jauge) => (
        <li key={jauge.id} className="flex items-center gap-1" title={jauge.nom}>
          <span aria-hidden="true">{jauge.icone}</span>
          <span className="tabular-nums">{gauges[jauge.id] ?? ''}</span>
          <span className="sr-only">{jauge.nom}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Le tableau de suivi : une ligne par îlot, rafraîchie toutes les quinze
 * secondes. L'enseignant y voit d'un coup d'œil qui avance, qui bloque, et qui
 * n'a pas encore commencé.
 */
export function SuiviDeClasse({
  content,
  client,
  classCode,
  pin,
  onProjection,
  onAnalyse,
  onClasseSupprimee,
}: {
  content: Content;
  client: ClientProf;
  classCode: string;
  pin: string;
  onProjection: () => void;
  onAnalyse: () => void;
  onClasseSupprimee: () => void;
}) {
  const [suivi, setSuivi] = useState<Suivi | null>(null);
  const [actualiseA, setActualiseA] = useState<Date | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /**
   * Instant de la dernière ouverture ou fermeture de chapitre.
   *
   * Un rafraîchissement parti *avant* ce changement rapporte encore l'ancienne
   * liste : l'appliquer annulerait le clic de l'enseignant sous ses yeux, pour
   * quinze secondes. On écarte donc les réponses plus vieilles que la dernière
   * écriture.
   */
  const derniereEcriture = useRef(0);

  const rafraichir = useCallback(async () => {
    const lanceA = Date.now();
    const resultat = await client.suivre(classCode, pin);
    if (resultat && lanceA >= derniereEcriture.current) {
      setSuivi(resultat);
      setActualiseA(new Date());
    }
  }, [client, classCode, pin]);

  useEffect(() => {
    void rafraichir();
    const minuteur = setInterval(() => void rafraichir(), INTERVALLE_RAFRAICHISSEMENT_MS);
    return () => clearInterval(minuteur);
  }, [rafraichir]);

  const basculerChapitre = async (chapterId: string): Promise<void> => {
    if (!suivi) return;
    const ouverts = new Set(suivi.chapitres);
    if (ouverts.has(chapterId)) {
      ouverts.delete(chapterId);
    } else {
      ouverts.add(chapterId);
    }
    const chapitres = content.chapitres.map((c) => c.id).filter((id) => ouverts.has(id));

    const resultat = await client.definirChapitres(classCode, pin, chapitres);
    if (resultat) {
      derniereEcriture.current = Date.now();
      setSuivi({ ...suivi, chapitres: resultat });
    }
  };

  /*
   * Deux gestes destructeurs, tous deux confirmés avant d'agir. Ils passent par
   * `derniereEcriture` comme le basculement de chapitre : un rafraîchissement
   * parti avant l'action rapporterait encore l'ancien tableau et ferait
   * réapparaître ce qu'on vient d'effacer, pour quinze secondes.
   */
  const remettreAZero = async (islandCode: string): Promise<void> => {
    if (!globalThis.confirm(T.prof.reinitialiserConfirmation)) return;

    if (await client.reinitialiserIlot(classCode, pin, islandCode)) {
      derniereEcriture.current = Date.now();
      setErreur(null);
      await rafraichir();
    } else {
      setErreur(T.prof.reinitialiserEchec);
    }
  };

  const supprimerLaClasse = async (): Promise<void> => {
    if (!globalThis.confirm(T.prof.supprimerClasseConfirmation)) return;

    if (await client.supprimerClasse(classCode, pin)) {
      onClasseSupprimee();
    } else {
      setErreur(T.prof.supprimerClasseEchec);
    }
  };

  const titreChapitre = (id: string | null): string =>
    content.chapitres.find((c) => c.id === id)?.titre ?? '';
  const titreEtape = (chapterId: string | null, stepIndex: number | null): string =>
    chapterId === null || stepIndex === null
      ? ''
      : (content.chapitres.find((c) => c.id === chapterId)?.deroule[stepIndex]?.titre ?? '');
  const titreSolution = (id: string | null): string => {
    if (id === null) return T.prof.aucuneSolution;
    const solution = content.solutions.find((s) => s.id === id);
    return solution ? '★'.repeat(solution.etoiles) : id;
  };

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            {T.prof.suivi} — {classCode}
          </h2>
          {actualiseA && (
            <p aria-live="polite" className="text-sm text-slate-600">
              {T.prof.rafraichi} {actualiseA.toLocaleTimeString('fr-FR')}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void rafraichir()}
            className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm"
          >
            {T.prof.rafraichirMaintenant}
          </button>
          <button
            type="button"
            disabled={!suivi}
            onClick={() =>
              suivi &&
              telechargerCsv(`neopolis-${classCode}.csv`, progressionEnCsv(content, suivi.ilots))
            }
            className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm disabled:opacity-40"
          >
            {T.prof.exporterCsv}
          </button>
          <button
            type="button"
            onClick={onAnalyse}
            className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm"
          >
            {T.prof.analyse}
          </button>
          <button
            type="button"
            onClick={onProjection}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.prof.projection}
          </button>
          <button
            type="button"
            onClick={() => void supprimerLaClasse()}
            className="min-h-touch rounded-xl px-4 font-semibold text-rose-700 ring-2 ring-rose-400 hover:bg-rose-50"
          >
            {T.prof.supprimerClasse}
          </button>
        </div>
      </header>

      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h3 className="font-bold text-slate-900">{T.prof.chapitres}</h3>
        <ul className="mt-2 flex flex-wrap gap-2">
          {content.chapitres.map((chapitre) => {
            const ouvert = suivi?.chapitres.includes(chapitre.id) ?? false;
            return (
              <li key={chapitre.id}>
                <button
                  type="button"
                  aria-pressed={ouvert}
                  // Tant que le suivi n'est pas chargé, on ignore quels
                  // chapitres sont ouverts : un clic ne ferait rien, et
                  // l'enseignant croirait à une panne.
                  disabled={suivi === null}
                  onClick={() => void basculerChapitre(chapitre.id)}
                  className={`min-h-touch rounded-xl border-2 px-4 font-semibold disabled:opacity-50 ${
                    ouvert
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  {chapitre.titre}
                  <span className="ml-2 text-sm font-normal">
                    {ouvert ? T.prof.fermerChapitre : T.prof.ouvrirChapitre}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {erreur && (
        <p role="alert" className="rounded-xl bg-rose-50 p-3 font-semibold text-rose-800">
          {erreur}
        </p>
      )}

      <div className="overflow-x-auto rounded-2xl border-2 border-slate-200 bg-white">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="p-3">{T.prof.colonneIlot}</th>
              <th className="p-3">{T.prof.colonneChapitre}</th>
              <th className="p-3">{T.prof.colonneEtape}</th>
              <th className="p-3">{T.prof.colonneJauges}</th>
              <th className="p-3">{T.prof.colonneSolution}</th>
              <th className="p-3">{T.prof.colonneTemps}</th>
              <th className="p-3">{T.prof.colonneActions}</th>
            </tr>
          </thead>
          <tbody>
            {(suivi?.ilots ?? []).map((ilot) => (
              <tr key={ilot.islandCode} className="border-b border-slate-200">
                <td className="p-3 font-mono font-bold tracking-widest">{ilot.islandCode}</td>
                <td className="p-3">
                  {ilot.chapterId ? (
                    titreChapitre(ilot.chapterId)
                  ) : (
                    <span className="text-slate-400">{T.prof.pasCommence}</span>
                  )}
                </td>
                <td className="p-3">{titreEtape(ilot.chapterId, ilot.stepIndex)}</td>
                <td className="p-3">
                  <JaugesMiniatures content={content} gauges={ilot.gauges} />
                </td>
                <td className="p-3 text-lg">{titreSolution(ilot.solutionDeployee)}</td>
                <td className="p-3 tabular-nums">{dureeLisible(ilot.tempsPasseSecondes)}</td>
                <td className="p-3">
                  <button
                    type="button"
                    onClick={() => void remettreAZero(ilot.islandCode)}
                    aria-label={`${T.prof.reinitialiserIlotDe} ${ilot.islandCode}`}
                    className="min-h-touch rounded-xl px-3 font-semibold text-rose-700 ring-2 ring-rose-300 hover:bg-rose-50"
                  >
                    {T.prof.reinitialiserIlot}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
